import { useMutation, useQueryClient } from '@tanstack/react-query';

import SafeReturnForm from 'components/patient/SafeReturnForm';
import safeReturnFormValidation from 'components/patient/safeReturnFormValidation';
import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import StandardModal, { ColorThemeName, PatientName } from 'components/shared/StandardModal';
import { FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import LocationEpisode from 'models/LocationEpisode';
import LocationEpisodeSummary from 'models/LocationEpisodeSummary';
import { ReviewType } from 'models/Review';
import { activityQueryKeys } from 'services/api/activity';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { createReview } from 'services/api/reviews';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

type RequestSafeReturnModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
  locationEpisode: LocationEpisode;
  snfLocationEpisode: LocationEpisodeSummary;
  patientName: string;
};

export type RequestSafeReturnFormValues = {
  note: ActivityInputValues;
  groupId: string;
};

export type RequestSafeReturnFormMeta = {
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
};

export default function RequestSafeReturnModal(props: RequestSafeReturnModalProps) {
  const { onCancel, onSuccess, patientName, locationEpisode, snfLocationEpisode } = props;
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const { mutate } = useMutation({
    mutationFn: createReview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show({ id: locationEpisode.id }) });
      queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
      onCancel();
    },
  });

  const handleSubmit = async (
    values: FormValues<RequestSafeReturnFormValues>,
    _meta: FormMeta<RequestSafeReturnFormMeta>,
    helpers: FormHelpers<RequestSafeReturnFormValues, RequestSafeReturnFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    delete values.note?.escalationType;

    mutate(
      {
        locationEpisodeId: locationEpisode.id,
        groupId: snfLocationEpisode.group.id,
        type: ReviewType.SAFE_RETURN,
        note: values.note
          ? {
              ...values.note,
              attachments: values.note.attachments?.map((upload) => upload.serialize()),
            }
          : undefined,
      },
      {
        onSuccess: () => {
          onSuccess();
          onCancel();
        },
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again' });
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  return (
    <StandardModal title='Request Safe Return' onCancel={onCancel} colorTheme={ColorThemeName.Success}>
      <PatientName>Patient: {patientName}</PatientName>

      <BodySmall>Requesting a safe return will notify all members of the co-management team.</BodySmall>

      <FormProvider<RequestSafeReturnFormValues, RequestSafeReturnFormMeta>
        onSubmit={handleSubmit}
        yupSchema={safeReturnFormValidation}
        resetOnInitialValuesChange>
        <SafeReturnForm
          onCancel={onCancel}
          locationEpisode={locationEpisode}
          noteFieldLabel='Why does the patient need to return to skilled care?'
        />
      </FormProvider>
    </StandardModal>
  );
}
